import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Select from 'react-select';

import { FormGroup } from '../../../shared/components/FormHandler/FormHandler';
import FormLabel from '../../../shared/components/FormLabel/FormLabel';
import SharedDatePicker from '../../../shared/_TBComponents/DatePicker';

const FormGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 2fr;
  column-gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DuplicateWarningText = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #e65100;
  margin: 12px 0 -20px;
`;

export const DuplicateWarning = () => (
  <DuplicateWarningText>
    Note: You already have a saved template with these exact settings.
  </DuplicateWarningText>
);

const FieldError = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: #e53935;
  margin-top: 4px;
`;

const FieldHint = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 400;
  color: #9e9e9e;
  margin-left: 6px;
`;

const FullWidthField = styled('div')`
  grid-column: 1 / -1;
  min-width: 0;
`;

const EqualGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RadioGroup = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin-top: 4px;
`;

const RadioLabel = styled('label')`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  cursor: pointer;
  color: #444;
  white-space: nowrap;
`;

const BottomSection = styled('div')`
  display: flex;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const BottomColumn = styled('div')`
  flex: 1;
  min-width: 0;
`;

export const categoryOptions = [
  { value: 'L', label: 'Lift' },
  { value: 'R', label: 'SAQ + C' },
  { value: 'C', label: 'Circuits' },
];

const genderOptions = [
  { value: 0, label: 'All' },
  { value: 1, label: 'Male' },
  { value: 2, label: 'Female' },
  { value: 3, label: 'Undefined' },
];

const rangeTypeOptions = [
  { value: 5, label: 'Current Max' },
  { value: 1, label: 'All Time' },
  { value: 2, label: 'Last 30 Days' },
  { value: 3, label: 'Today' },
  { value: 4, label: 'Custom' },
];

const compareToOptions = [
  { value: 0, label: 'None' },
  { value: 5, label: 'Current Max' },
  { value: 1, label: 'All Time' },
  { value: 2, label: 'Last 30 Days' },
  { value: 3, label: 'Today' },
  { value: 4, label: 'Custom' },
];

export const validateLeaderboardForm = (values) => {
  const errors = {};
  if (!values.category) errors.category = 'Exercise type is required.';
  if (!values.exerciseId) errors.exerciseId = 'Exercise is required.';
  if (values.gender === undefined || values.gender === null || values.gender === '') {
    errors.gender = 'Gender is required.';
  }
  if (values.rangeType === undefined) errors.rangeType = 'Time frame is required.';
  if (values.rangeType === 4 && (!values.rangeDates?.startDate || !values.rangeDates?.endDate)) {
    errors.rangeDates = 'Please select a start and end date.';
  }
  if (values.compareTo === 4
    && (!values.compareDates?.startDate || !values.compareDates?.endDate)) {
    errors.compareDates = 'Please select a start and end date.';
  }
  return errors;
};

const LeaderboardFormFields = ({
  exercises,
  exercisesError,
  groups,
  formProps,
  isLoadingExercises,
  isLoadingGroups,
  onCategoryChange,
}) => {
  const {
    values, errors, touched, setFieldValue, setFieldTouched,
  } = formProps;

  const currentCategoryOption = categoryOptions.find((c) => c.value === values.category) || null;
  const currentExerciseOption = exercises.find((e) => e.id === values.exerciseId) || null;
  const currentGenderOption = genderOptions.find((g) => g.value === values.gender) || null;
  const currentRangeTypeOption = rangeTypeOptions.find((r) => r.value === values.rangeType) || null;
  const currentCompareToOption = compareToOptions.find((c) => c.value === values.compareTo) || null;

  return (
    <>
      <FormGrid>
        <FormGroup>
          <FormLabel>Exercise Type</FormLabel>
          <Select
            value={currentCategoryOption}
            options={categoryOptions}
            onChange={(option) => {
              setFieldValue('category', option.value);
              setFieldValue('exerciseId', 0);
              setFieldValue('exerciseName', '');
              onCategoryChange(option.value);
            }}
            onBlur={() => setFieldTouched('category', true)}
          />
          {touched.category && errors.category && (
            <FieldError>{errors.category}</FieldError>
          )}
        </FormGroup>

        <FormGroup>
          <FormLabel>Exercise</FormLabel>
          <Select
            value={currentExerciseOption}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.id}
            isDisabled={!values.category || exercises.length === 0}
            isLoading={isLoadingExercises}
            options={exercises}
            onChange={(option) => {
              setFieldValue('exerciseId', option.id);
              setFieldValue('exerciseName', option.name);
            }}
            onBlur={() => setFieldTouched('exerciseId', true)}
          />
          {touched.exerciseId && errors.exerciseId && (
            <FieldError>{errors.exerciseId}</FieldError>
          )}
          {!errors.exerciseId && exercisesError && (
            <FieldError>{exercisesError}</FieldError>
          )}
        </FormGroup>

        <FullWidthField>
          <FormGroup>
            <FormLabel>
              Groups
              <FieldHint>If left blank, all available groups will be included.</FieldHint>
            </FormLabel>
            <Select
              value={values.groups}
              getOptionLabel={(o) => o.name}
              getOptionValue={(o) => o.id}
              isDisabled={isLoadingGroups}
              isLoading={isLoadingGroups}
              options={groups}
              onChange={(option) => setFieldValue('groups', option || [])}
              onBlur={() => setFieldTouched('groups', true)}
              isMulti
              styles={{
                multiValue: (base) => ({ ...base, maxWidth: '200px' }),
                multiValueLabel: (base) => ({
                  ...base,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }),
              }}
            />
            {touched.groups && errors.groups && (
              <FieldError>{errors.groups}</FieldError>
            )}
          </FormGroup>
        </FullWidthField>

        <FullWidthField>
          <EqualGrid>
            <FormGroup>
              <FormLabel>Include Athletes That Match</FormLabel>
              <RadioGroup>
                <RadioLabel>
                  <input
                    type='radio'
                    name='mustBeInAllGroups'
                    checked={!values.mustBeInAllGroups}
                    onChange={() => setFieldValue('mustBeInAllGroups', false)}
                  />
                  Any selected group
                </RadioLabel>
                <RadioLabel>
                  <input
                    type='radio'
                    name='mustBeInAllGroups'
                    checked={values.mustBeInAllGroups}
                    onChange={() => setFieldValue('mustBeInAllGroups', true)}
                  />
                  All selected groups
                </RadioLabel>
              </RadioGroup>
            </FormGroup>

            <FormGroup>
              <FormLabel>Gender</FormLabel>
              <Select
                value={currentGenderOption}
                options={genderOptions}
                onChange={(option) => setFieldValue('gender', option.value)}
                onBlur={() => setFieldTouched('gender', true)}
              />
              {touched.gender && errors.gender && (
                <FieldError>{errors.gender}</FieldError>
              )}
            </FormGroup>
          </EqualGrid>
        </FullWidthField>
      </FormGrid>

      <BottomSection>
        <BottomColumn>
          <FormGroup>
            <FormLabel>Time Frame/Source Max</FormLabel>
            <Select
              value={currentRangeTypeOption}
              options={rangeTypeOptions}
              onChange={(option) => {
                setFieldValue('rangeType', option.value);
                if (option.value !== 4) setFieldValue('rangeDates', {});
              }}
              onBlur={() => setFieldTouched('rangeType', true)}
            />
            {touched.rangeType && errors.rangeType && (
              <FieldError>{errors.rangeType}</FieldError>
            )}
          </FormGroup>
          {values.rangeType === 4 && (
            <FormGroup>
              <FormLabel>Pick a Date Range</FormLabel>
              <SharedDatePicker
                singleDate={false}
                fullWidth
                startDate={values.rangeDates.startDate || null}
                endDate={values.rangeDates.endDate || null}
                setStartDate={(date) => {
                  const endDate = date > values.rangeDates.endDate
                    ? null : values.rangeDates.endDate;
                  setFieldValue('rangeDates', { startDate: date, endDate });
                }}
                setEndDate={(date) => setFieldValue('rangeDates', { ...values.rangeDates, endDate: date })}
                maxDate={new Date(2099, 11, 31)}
              />
              {errors.rangeDates && <FieldError>{errors.rangeDates}</FieldError>}
            </FormGroup>
          )}
        </BottomColumn>

        <BottomColumn>
          <FormGroup>
            <FormLabel>Compare To</FormLabel>
            <Select
              value={currentCompareToOption}
              options={compareToOptions}
              onChange={(option) => {
                setFieldValue('compareTo', option.value);
                if (option.value !== 4) setFieldValue('compareDates', {});
              }}
            />
          </FormGroup>
          {values.compareTo === 4 && (
            <FormGroup>
              <FormLabel>Pick a Date Range</FormLabel>
              <SharedDatePicker
                singleDate={false}
                fullWidth
                startDate={values.compareDates.startDate || null}
                endDate={values.compareDates.endDate || null}
                setStartDate={(date) => {
                  const endDate = date > values.compareDates.endDate
                    ? null : values.compareDates.endDate;
                  setFieldValue('compareDates', { startDate: date, endDate });
                }}
                setEndDate={(date) => setFieldValue('compareDates', { ...values.compareDates, endDate: date })}
                maxDate={new Date(2099, 11, 31)}
              />
              {errors.compareDates && <FieldError>{errors.compareDates}</FieldError>}
            </FormGroup>
          )}
        </BottomColumn>
      </BottomSection>
    </>
  );
};

LeaderboardFormFields.defaultProps = {
  exercisesError: null,
};

LeaderboardFormFields.propTypes = {
  exercises: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })).isRequired,
  groups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })).isRequired,
  formProps: PropTypes.shape({
    values: PropTypes.shape({
      category: PropTypes.string,
      exerciseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      groups: PropTypes.arrayOf(PropTypes.shape({})),
      mustBeInAllGroups: PropTypes.bool,
      gender: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      rangeType: PropTypes.number,
      rangeDates: PropTypes.shape({
        startDate: PropTypes.shape({}),
        endDate: PropTypes.shape({}),
      }),
      compareTo: PropTypes.number,
      compareDates: PropTypes.shape({
        startDate: PropTypes.shape({}),
        endDate: PropTypes.shape({}),
      }),
    }),
    errors: PropTypes.shape({
      category: PropTypes.string,
      exerciseId: PropTypes.string,
      groups: PropTypes.string,
      gender: PropTypes.string,
      rangeType: PropTypes.string,
      rangeDates: PropTypes.string,
      compareDates: PropTypes.string,
    }),
    touched: PropTypes.shape({
      category: PropTypes.bool,
      exerciseId: PropTypes.bool,
      groups: PropTypes.bool,
      gender: PropTypes.bool,
      rangeType: PropTypes.bool,
    }),
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
  }).isRequired,
  exercisesError: PropTypes.string,
  isLoadingExercises: PropTypes.bool.isRequired,
  isLoadingGroups: PropTypes.bool.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
};

export default LeaderboardFormFields;
